import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Administrations from "@/pages/Administrations";
import Logement from "@/pages/Logement";
import Documents from "@/pages/Documents";
import Demarches from "@/pages/Demarches";
import BienEtre from "@/pages/BienEtre";
import Referral from "@/pages/Referral";
import JobsArgent from "@/pages/JobsArgent";
import Communaute from "@/pages/Communaute";
import Premium from "@/pages/Premium";
import PremiumSuccess from "@/pages/PremiumSuccess";
import Modeles from "@/pages/Modeles";
import AIToolsJobs from "@/pages/AIToolsJobs";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import AdminBroadcast from "@/pages/AdminBroadcast";
import AdminObservability from "@/pages/AdminObservability";
import DigestHistory from "@/pages/DigestHistory";
import VerifyEmail from "@/pages/VerifyEmail";
import LandingEtudiants from "@/pages/LandingEtudiants";
import LandingStudentsEN from "@/pages/LandingStudentsEN";
import Securite from "@/pages/Securite";
import EtudiantsInternationaux from "@/pages/EtudiantsInternationaux";
import AccessibilityToolbar from "@/components/AccessibilityToolbar";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import NewsletterUnsubscribe from "@/pages/NewsletterUnsubscribe";
import AuthCallback from "@/pages/AuthCallback";
import AccessibiliteAccompagnement from "@/pages/AccessibiliteAccompagnement";
import ChatCommunaute from "@/pages/ChatCommunaute";
import CGU from "@/pages/legal/CGU";
import Confidentialite from "@/pages/legal/Confidentialite";
import Mentions from "@/pages/legal/Mentions";
import CookiesPage from "@/pages/legal/Cookies";
import Remboursement from "@/pages/legal/Remboursement";
import Retractation from "@/pages/legal/Retractation";
import Layout from "@/components/Layout";
import PillarLogement from "@/pages/PillarLogement";
import SourcesOfficielles from "@/pages/SourcesOfficielles";
import Signaler from "@/pages/Signaler";
import CookieBanner from "@/components/CookieBanner";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-indigo-500" size={28} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Root() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Synchronous check during render (NOT in useEffect) to avoid race conditions
  // with AuthProvider. Detect Emergent OAuth callback fragment and process it FIRST.
  if (typeof location.hash === "string" && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Root />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/landing/etudiants" element={<LandingEtudiants />} />
      <Route path="/en/students" element={<LandingStudentsEN />} />
      <Route path="/securite" element={<Securite />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      {/* SEO pillar pages — long-form guides, top of silos */}
      <Route path="/guide/logement-etudiant-france" element={<PillarLogement />} />
      <Route path="/sources-officielles" element={<SourcesOfficielles />} />
      <Route path="/signaler" element={<Signaler />} />
      <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
      {/* Public legal pages */}
      <Route path="/legal/cgu" element={<CGU />} />
      <Route path="/legal/confidentialite" element={<Confidentialite />} />
      <Route path="/legal/mentions" element={<Mentions />} />
      <Route path="/legal/cookies" element={<CookiesPage />} />
      <Route path="/legal/remboursement" element={<Remboursement />} />
      <Route path="/legal/retractation" element={<Retractation />} />
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/administrations" element={<Administrations />} />
        <Route path="/logement" element={<Logement />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/demarches" element={<Demarches />} />
        <Route path="/bien-etre" element={<BienEtre />} />
        <Route path="/accessibilite" element={<AccessibiliteAccompagnement />} />
        <Route path="/accessibilite-et-accompagnement" element={<AccessibiliteAccompagnement />} />
        <Route path="/etudiants-internationaux" element={<EtudiantsInternationaux />} />
        <Route path="/jobs-argent" element={<JobsArgent />} />
        <Route path="/communaute" element={<Communaute />} />
        <Route path="/chat" element={<ChatCommunaute />} />
        <Route path="/premium" element={<Premium />} />
        <Route path="/premium/success" element={<PremiumSuccess />} />
        <Route path="/modeles" element={<Modeles />} />
        <Route path="/jobs-ai" element={<AIToolsJobs />} />
        <Route path="/parrainage" element={<Referral />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/broadcast" element={<AdminBroadcast />} />
        <Route path="/admin/observability" element={<AdminObservability />} />
        <Route path="/notifications/history" element={<DigestHistory />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <AccessibilityToolbar />
          <CookieBanner />
        </BrowserRouter>
        <Toaster richColors position="top-right" toastOptions={{ duration: 3000, style: { marginTop: "56px" } }} />
      </AuthProvider>
    </div>
  );
}

export default App;
